import React, { useCallback, useMemo, useState } from "react";
import { Box, Button, CircularProgress, IconButton, Stack, Typography } from "@mui/material";
import { ChevronLeft, ChevronRight, Download } from "@mui/icons-material";
import moment from "moment";
import { useParams } from "react-router-dom";
import toast from "react-hot-toast";
import { useStoresSummaryStoresOnly } from "features/Stores";
import { useDateFilter } from "../../../../hooks/useFilterContext";
import {
  useAuditDashboardStoreAuditSummaries,
  useAuditDashboardStoreBackstockLedger,
  useAuditDashboardStoreScratcherReconciliationAuditsRange,
  useAuditDashboardShiftCashierNotes,
  useAuditDashboardManualSoldOutReport,
  useAuditDashboardManualSoldOutImpact,
} from "../../hooks";
import { auditToneStyles, getAuditTone } from "../../theme";
import { isAuditDayInProgress } from "../../utils/auditDayProgress";
import { getAuditDataAvailability } from "../../utils/auditMissingState";
import AuditDetailSummarySection from "./AuditDetailSummarySection";
import AuditShiftBreakdownSection from "./AuditShiftBreakdownSection";
import AuditDetailInventorySection from "./AuditDetailInventorySection";
import AuditDayInProgressEmptyState from "./AuditDayInProgressEmptyState";
import { useAcknowledgeAndVerifiedDetails } from "../../../audits/hooks";
import ReconciliationReviewSection from "./ReconciliationReviewSection";
import { shouldShowReconciliationReview } from "./auditDetailReconciliationVisibility";
import { generateAuditedGamePacksCsv } from "../../utils/auditedGamePacksCsv";
import { buildDailyCashierShiftNotes } from "../../utils/cashierShiftNotes";
import { hasScratcherMismatch } from "../../utils/scratcherTableUtils";

interface AuditDetailViewProps {
  selectedReportDate: string | null;
  onPrevDate?: () => void;
  onNextDate?: () => void;
  canGoPrev?: boolean;
  canGoNext?: boolean;
}

const formatDetailRangeLabel = (startDate?: string | null, endDate?: string | null) => {
  if (!startDate || !endDate) {
    return "Audit Detail";
  }

  return `${moment(startDate).format("MMM D")} - ${moment(endDate).format("MMM D, YYYY")}`;
};

const formatReportedScratcherRangeLabel = (startDate?: string | null, endDate?: string | null) => {
  if (!startDate || !endDate) {
    return "";
  }

  if (moment(startDate).isSame(moment(endDate), "day")) {
    return moment(startDate).format("MM/DD");
  }

  return `${moment(startDate).format("MM/DD")} - ${moment(endDate).format("M/DD")}`;
};

export default function AuditDetailView({
  selectedReportDate,
  onPrevDate,
  onNextDate,
  canGoPrev = false,
  canGoNext = false,
}: AuditDetailViewProps) {
  const { id: storeId } = useParams<{ id: string }>();
  const { startDate, endDate } = useDateFilter();
  const [isDownloadingCsv, setIsDownloadingCsv] = useState(false);

  const auditSummaryQuery = useAuditDashboardStoreAuditSummaries(
    storeId && startDate && endDate ? { storeId, startDate, endDate } : null
  );
  const backstockLedgerQuery = useAuditDashboardStoreBackstockLedger(
    storeId && startDate && endDate ? { storeId, startDate, endDate } : null
  );
  const { data: allStores = [] } = useStoresSummaryStoresOnly(undefined, {
    enabled: Boolean(storeId),
  });
  const scratcherAuditWindowsQuery = useAuditDashboardStoreScratcherReconciliationAuditsRange(
    storeId,
    startDate,
    endDate
  );
  const storeDetails = useMemo(() => {
    if (!storeId) return undefined;
    return allStores.find((item) => item.store.id === storeId)?.store;
  }, [allStores, storeId]);
  const hasPosType = Boolean(storeDetails?.pos_type && storeDetails.pos_type !== "n/a");
  const isLottoAuthorized = Boolean(storeDetails?.lotto_authorized);
  const isRangeSummary = selectedReportDate === "summary";
  const selectedDetailDate = !isRangeSummary && selectedReportDate ? selectedReportDate : null;
  const manualSoldOutReportQuery = useAuditDashboardManualSoldOutReport(storeId, selectedDetailDate);
  const manualSoldOutImpactQuery = useAuditDashboardManualSoldOutImpact(storeId, selectedDetailDate);
  const acknowledgeAndVerifiedQuery = useAcknowledgeAndVerifiedDetails(storeId, selectedDetailDate);
  const storeUserRole = String((storeDetails as any)?.user_role || "").toLowerCase();
  const canApproveReconciliation = Boolean((storeDetails as any)?.can_approve_reconciliation);
  const detailRangeLabel = formatDetailRangeLabel(auditSummaryQuery.data?.startDate, auditSummaryQuery.data?.endDate);
  const reportedScratcherRangeLabel = formatReportedScratcherRangeLabel(
    auditSummaryQuery.data?.startDate,
    auditSummaryQuery.data?.endDate
  );

  const selectedDay = useMemo(() => {
    if (isRangeSummary) {
      return auditSummaryQuery.data?.overallTotals ?? null;
    }

    const dailySummaries = auditSummaryQuery.data?.dailySummaries ?? [];

    if (selectedReportDate) {
      const matchedDay = dailySummaries.find((day) => day.reportDate === selectedReportDate);
      if (matchedDay) {
        return matchedDay;
      }
    }

    return dailySummaries[0] ?? null;
  }, [auditSummaryQuery.data?.dailySummaries, auditSummaryQuery.data?.overallTotals, isRangeSummary, selectedReportDate]);

  const headerLabel = isRangeSummary ? detailRangeLabel : selectedDay ? moment(selectedDay.reportDate).format("ddd, M/D/YYYY") : "Audit Detail";
  const auditCount = selectedDay?.auditTotals?.audit?.audit_count ?? 0;
  const headerVariance = selectedDay?.auditTotals?.reconciliation_variance?.total_variance;
  const isDayInProgress = isAuditDayInProgress(selectedDay?.reportDate);
  const showDayInProgressEmptyState = !isRangeSummary && isDayInProgress && auditCount === 0;
  const selectedDayAvailability = useMemo(
    () => selectedDay
      ? getAuditDataAvailability(selectedDay, { hasPosType, isLottoAuthorized, isDayInProgress })
      : null,
    [hasPosType, isDayInProgress, isLottoAuthorized, selectedDay]
  );
  const headerMissingSources = isDayInProgress
    ? []
    : selectedDayAvailability?.missingSources ?? [];
  const hasHeaderMissingSources = headerMissingSources.length > 0;
  const headerTone = isDayInProgress || hasHeaderMissingSources ? "progress" : getAuditTone(headerVariance);
  const headerToneStyles = auditToneStyles[headerTone];
  const headerMissingBadges = headerMissingSources.map((source) => `${source} Missing`);
  const acknowledgeAndVerified = acknowledgeAndVerifiedQuery.data;
  const showReconciliationReview = Boolean(
    !isRangeSummary &&
    selectedDay &&
    shouldShowReconciliationReview(selectedDay, acknowledgeAndVerified)
  );

  const selectedBackstockDay = useMemo(() => {
    if (isRangeSummary) {
      return backstockLedgerQuery.data?.overall ?? null;
    }

    const reportDate = selectedDay?.reportDate ?? null;
    if (!reportDate) {
      return null;
    }

    return backstockLedgerQuery.data?.daily.find((day) => day.reportDate === reportDate) ?? null;
  }, [backstockLedgerQuery.data?.daily, backstockLedgerQuery.data?.overall, isRangeSummary, selectedDay?.reportDate]);

  const selectedScratcherAuditWindowText = isRangeSummary
    ? scratcherAuditWindowsQuery.data?.getRangeWindowText(
        auditSummaryQuery.data?.startDate,
        auditSummaryQuery.data?.endDate
      ) ?? "-"
    : selectedDay
      ? scratcherAuditWindowsQuery.data?.getWindowText(selectedDay.reportDate) ?? "-"
      : "-";

  const isLoading = auditSummaryQuery.isLoading || backstockLedgerQuery.isLoading;
  const hasError = auditSummaryQuery.isError || backstockLedgerQuery.isError;
  const csvReportDate = isRangeSummary
    ? null
    : selectedDetailDate ?? selectedDay?.reportDate ?? null;
  const csvStartDate = csvReportDate ?? startDate;
  const csvEndDate = csvReportDate ?? endDate;
  const csvShifts = useMemo(() => {
    const shifts = scratcherAuditWindowsQuery.data?.shifts ?? [];
    if (!csvReportDate) {
      return shifts;
    }

    return shifts.filter((shift) => shift.reportDate === csvReportDate);
  }, [csvReportDate, scratcherAuditWindowsQuery.data?.shifts]);
  const selectedDayShifts = useMemo(() => {
    if (!selectedDetailDate) {
      return [];
    }

    return (scratcherAuditWindowsQuery.data?.shifts ?? [])
      .filter((shift) => shift.reportDate === selectedDetailDate);
  }, [scratcherAuditWindowsQuery.data?.shifts, selectedDetailDate]);
  const selectedDayAuditIds = useMemo(
    () => selectedDayShifts.map((shift) => String(shift.auditId)),
    [selectedDayShifts]
  );
  const scratcherVariance = selectedDay?.auditTotals?.reconciliation_variance?.scratcher;
  const shouldLoadDailyCashierShiftNotes = Boolean(
    !isRangeSummary
    && hasPosType
    && selectedDayAvailability?.canShowScratcherVariance
    && hasScratcherMismatch(
      scratcherVariance?.quantity_variance,
      scratcherVariance?.revenue_variance
    )
  );
  const shiftCashierNotes = useAuditDashboardShiftCashierNotes(
    storeId,
    selectedDayAuditIds,
    shouldLoadDailyCashierShiftNotes
  );
  const dailyCashierShiftNotes = useMemo(() => {
    if (isRangeSummary || !selectedDetailDate) {
      return [];
    }

    return buildDailyCashierShiftNotes(
      shiftCashierNotes,
      selectedDayShifts,
      selectedDetailDate
    );
  }, [isRangeSummary, selectedDayShifts, selectedDetailDate, shiftCashierNotes]);
  const handleDownloadCsv = useCallback(async () => {
    if (!storeId || !csvStartDate || !csvEndDate || isDownloadingCsv) {
      return;
    }

    setIsDownloadingCsv(true);
    try {
      const file = await generateAuditedGamePacksCsv({
        storeId,
        storeName: auditSummaryQuery.data?.storeName ?? storeDetails?.name ?? null,
        startDate: csvStartDate,
        endDate: csvEndDate,
        shifts: csvShifts,
      });
      const url = URL.createObjectURL(file.blob);
      const link = document.createElement("a");
      try {
        link.href = url;
        link.download = file.filename;
        document.body.appendChild(link);
        link.click();
      } finally {
        link.remove();
        URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error("Failed to download audited game pack CSV:", error);
      toast.error("Unable to download the scratcher log.");
    } finally {
      setIsDownloadingCsv(false);
    }
  }, [
    auditSummaryQuery.data?.storeName,
    csvEndDate,
    csvShifts,
    csvStartDate,
    isDownloadingCsv,
    storeDetails?.name,
    storeId,
  ]);

  return (
    <Box
      sx={{
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 2,
        overflow: "hidden",
        bgcolor: "#F1F5FA",
        minHeight: { xs: 280, lg: "100%" },
        minWidth: 0,
      }}
    >
      <Box
        sx={{
          px: 2,
          py: 1.5,
          borderBottom: "1px solid",
          borderColor: "divider",
          bgcolor: headerToneStyles.bg,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            minWidth: 0,
            flexWrap: { xs: "wrap", sm: "nowrap" },
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0, flexWrap: "wrap", rowGap: 0.75 }}>
            <Typography
              component="h2"
              variant="subtitle1"
              sx={{ fontSize: 22, fontWeight: 800, lineHeight: 1.2, minWidth: 0, color: "#222b45", overflowWrap: "anywhere" }}
            >
              {headerLabel}
            </Typography>
            {headerMissingBadges.map((badge) => (
              <Box
                key={badge}
                component="span"
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  height: 26,
                  px: 1,
                  borderRadius: 999,
                  color: "#8a5a12",
                  bgcolor: "#fff7ea",
                  border: "1px solid rgba(223, 148, 0, 0.22)",
                  fontSize: 11,
                  fontWeight: 600,
                  lineHeight: 1,
                  whiteSpace: "nowrap",
                }}
              >
                {badge}
              </Box>
            ))}
            {isDayInProgress ? (
              <Box
                component="span"
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  height: 26,
                  px: 1,
                  borderRadius: 999,
                  color: auditToneStyles.progress.text,
                  bgcolor: "#fff7ea",
                  border: "1px solid rgba(223, 148, 0, 0.22)",
                  fontSize: 11,
                  fontWeight: 600,
                  lineHeight: 1,
                  whiteSpace: "nowrap",
                }}
              >
                Day In Progress
              </Box>
            ) : null}
            <Box
              component="span"
              sx={{
                display: "inline-flex",
                alignItems: "center",
                height: 26,
                px: 1,
                borderRadius: 999,
                color: "#047857",
                bgcolor: "#effaf3",
                border: "1px solid rgba(5, 190, 67, 0.22)",
                fontSize: 13,
                fontWeight: 600,
                lineHeight: 1,
                whiteSpace: "nowrap",
                flexShrink: 0,
              }}
            >
              {`${auditCount} ${auditCount === 1 ? "Audit" : "Audits"}`}
            </Box>
          </Stack>
          <Stack direction="row" spacing={0.5} sx={{ flexShrink: 0, ml: "auto" }}>
            <Button
              type="button"
              variant="outlined"
              size="small"
              onClick={handleDownloadCsv}
              disabled={
                isDownloadingCsv ||
                scratcherAuditWindowsQuery.isLoading ||
                !storeId ||
                !csvStartDate ||
                !csvEndDate ||
                !csvShifts.length
              }
              startIcon={
                isDownloadingCsv
                  ? <CircularProgress size={14} />
                  : <Download fontSize="small" />
              }
              sx={{
                bgcolor: "background.paper",
                textTransform: "none",
                whiteSpace: "nowrap",
              }}
            >
              {isDownloadingCsv ? "Preparing Log" : "Download Scratcher Log"}
            </Button>
            <IconButton
              size="small"
              onClick={onPrevDate}
              disabled={isRangeSummary || !canGoPrev}
              sx={{ border: "1px solid", borderColor: "divider", bgcolor: "background.paper" }}
            >
              <ChevronLeft fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              onClick={onNextDate}
              disabled={isRangeSummary || !canGoNext}
              sx={{ border: "1px solid", borderColor: "divider", bgcolor: "background.paper" }}
            >
              <ChevronRight fontSize="small" />
            </IconButton>
          </Stack>
        </Box>
      </Box>

      <Box
        sx={{
          p: 1.5,
          minHeight: 0,
          minWidth: 0,
          overflowX: "auto",
          overscrollBehaviorX: "contain",
          scrollbarWidth: "thin",
        }}
      >
        <Box sx={{ display: "grid", gap: 2, minWidth: { xs: 720, md: 780 } }}>
          {isLoading ? (
            <Box
              sx={{
                minHeight: 320,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <CircularProgress size={28} />
            </Box>
          ) : hasError ? (
            <Box
              sx={{
                minHeight: 320,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Typography variant="body2" color="text.secondary">
                Unable to load audit details.
              </Typography>
            </Box>
          ) : (
            <>
              {showDayInProgressEmptyState ? (
                <AuditDayInProgressEmptyState />
              ) : (
                <>
                  <AuditDetailSummarySection
                    day={selectedDay}
                    storeId={storeId}
                    scratcherAuditWindowText={selectedScratcherAuditWindowText}
                    title={isRangeSummary ? `Summary from ${detailRangeLabel}` : undefined}
                    isRangeSummary={isRangeSummary}
                    reportedScratcherDateLabel={isRangeSummary ? reportedScratcherRangeLabel : undefined}
                    inventoryStartDate={isRangeSummary ? auditSummaryQuery.data?.startDate : undefined}
                    inventoryEndDate={isRangeSummary ? auditSummaryQuery.data?.endDate : undefined}
                    hasPosType={hasPosType}
                    isLottoAuthorized={isLottoAuthorized}
                    backstockLedgerDay={selectedBackstockDay}
                    manualSoldOutReport={manualSoldOutReportQuery.data ?? null}
                    manualSoldOutImpact={manualSoldOutImpactQuery.data ?? null}
                    barcodeFormat={(storeDetails as any)?.barcode_format}
                    cashierShiftNotes={dailyCashierShiftNotes}
                    dayStatusSection={showReconciliationReview && selectedDetailDate ? (
                      <ReconciliationReviewSection
                        storeId={storeId}
                        reportDate={selectedDetailDate}
                        storeUserRole={storeUserRole}
                        canApproveReconciliation={canApproveReconciliation}
                        detail={acknowledgeAndVerified}
                        summaryVerified={selectedDay?.verified ?? null}
                        embedded
                      />
                    ) : undefined}
                  />
                  <Box sx={{ borderTop: "1px solid", borderColor: "divider" }} />
                  <AuditShiftBreakdownSection
                    day={selectedDay}
                    startDate={isRangeSummary ? auditSummaryQuery.data?.startDate : undefined}
                    endDate={isRangeSummary ? auditSummaryQuery.data?.endDate : undefined}
                    hasPosType={hasPosType}
                  />
                  <Box sx={{ borderTop: "1px solid", borderColor: "divider" }} />
                  <AuditDetailInventorySection
                    backstockLedgerDay={selectedBackstockDay}
                    reportDate={isRangeSummary ? undefined : selectedDay?.reportDate}
                    startDate={isRangeSummary ? auditSummaryQuery.data?.startDate : undefined}
                    endDate={isRangeSummary ? auditSummaryQuery.data?.endDate : undefined}
                    isDayInProgress={isDayInProgress}
                    hasSubmittedAudits={auditCount > 0}
                  />
                </>
              )}
            </>
          )}
        </Box>
      </Box>
    </Box>
  );
}
